import { useState, useEffect } from "react";
import { Camera, CheckCircle, AlertCircle, User, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ANPRSimulationProps {
  onPlateDetected: (plateNumber: string) => void;
  onFaceDetected: (confidence: number) => void;
}

export default function ANPRSimulation({ onPlateDetected, onFaceDetected }: ANPRSimulationProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [detectedPlate, setDetectedPlate] = useState<string>("");
  const [faceConfidence, setFaceConfidence] = useState<number>(0);
  const [scanProgress, setScanProgress] = useState(0);

  const mockPlates = ["ABC-123GP", "XYZ-789LM", "DEF-456RT", "GHI-012WE", "JKL-345QQ"];

  const simulateANPR = () => {
    setIsScanning(true);
    setScanProgress(0);
    setDetectedPlate("");
    setFaceConfidence(0);

    // Simulate scanning progress
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsScanning(false);
          
          // Simulate plate detection
          const randomPlate = mockPlates[Math.floor(Math.random() * mockPlates.length)];
          setDetectedPlate(randomPlate);
          onPlateDetected(randomPlate);
          
          // Simulate face detection
          const confidence = Math.floor(Math.random() * 30) + 70; // 70-100%
          setFaceConfidence(confidence);
          onFaceDetected(confidence);
          
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Camera className="w-5 h-5 mr-2" />
            ANPR & Face Recognition
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Camera Feed Simulation */}
            <div className="bg-gray-900 rounded-lg p-8 text-center relative overflow-hidden">
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="text-green-400 text-sm mb-2">CAMERA FEED - LANE 1</div>
                <div className="text-white text-6xl">📹</div>
                <div className="text-gray-400 text-sm mt-2">Live Video Stream</div>
              </div>
              
              {isScanning && (
                <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                  <div className="bg-blue-600 text-white px-4 py-2 rounded-lg">
                    Scanning... {scanProgress}%
                  </div>
                </div>
              )}
              
              {detectedPlate && (
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-lg text-sm">
                  <Car className="w-4 h-4 inline mr-1" />
                  {detectedPlate}
                </div>
              )}
              
              {faceConfidence > 0 && (
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-lg text-sm">
                  <User className="w-4 h-4 inline mr-1" />
                  {faceConfidence}%
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex justify-center">
              <Button 
                onClick={simulateANPR}
                disabled={isScanning}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="button-simulate-anpr"
              >
                {isScanning ? "Scanning..." : "Simulate Vehicle Detection"}
              </Button>
            </div>

            {/* Detection Results */}
            {detectedPlate && (
              <div className="space-y-2">
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span className="font-medium">License Plate Detected: {detectedPlate}</span>
                </div>
                
                <div className="flex items-center text-blue-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span className="font-medium">Face Recognition: {faceConfidence}% confidence</span>
                </div>

                {faceConfidence < 80 && (
                  <div className="flex items-center text-orange-600">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm">Low face recognition confidence - manual verification recommended</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}